import { useCallback, useEffect, useRef, useState } from "react";
import createTunnus from "@yleisradio/tunnus-sdk";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { isYleEmbed } from "../../helpers/commonUtils";
import { setWindowProperties } from "../../service/window";
import { LoginData } from "../../types/common";

const defaultLoginData = {
  loggedIn: false,
  residenceInFinland: false,
  userId: undefined
};

export const tunnusExists = (): boolean =>
  window.ylePlayer.tunnus !== undefined;

const initTunnus = (language: string, theme: string): void => {
  if (tunnusExists()) {
    return;
  }

  const tunnus = createTunnus({
    environment: __YLE_TUNNUS_SDK_ENV,
    initiatingApp: "player-static",
    language: language === "swe" ? "sv" : "fi",
    returnToUrl: window.location.href,
    theme: theme === "light" ? "light" : "dark",
    trackingAppName: `player_static_${__YLE_TUNNUS_SDK_ENV === "production" ? "prod" : "test"}`
  });

  setWindowProperties({ tunnus });
};

export const useLoginData = ({
  requireLogin
}: {
  requireLogin: boolean;
}): LoginData | undefined => {
  const loginInitialized = useRef(false);

  const { language, theme } = useEmbedParametersContext();

  const [initialLoginData, setInitialLoginData] = useState<LoginData>();

  const loadLoginData = useCallback(async () => {
    // If login is not required, use default (not logged in) data
    if (!requireLogin || !isYleEmbed()) {
      setInitialLoginData({ ...defaultLoginData });
      return;
    }

    initTunnus(language, theme);

    // Fetch user info if status is unknown or the user is logged in (to ensure data is up-to-date)
    if (window.ylePlayer.loginData === undefined) {
      window.ylePlayer.loginData = new Promise(resolve => {
        window.ylePlayer.tunnus
          .getUserInfo()
          .then((userInfo: UserInfo) => {
            if (userInfo) {
              resolve({
                loggedIn: true,
                residenceInFinland: !!userInfo?.residenceInFinland,
                userId: userInfo.userId
              });
            } else {
              resolve(defaultLoginData);
            }
          })
          .catch(() => resolve(defaultLoginData));
      });
    }
    window.ylePlayer.loginData
      .then(loginData => setInitialLoginData(loginData))
      .catch(() => setInitialLoginData(defaultLoginData));
  }, [language, requireLogin, theme]);

  useEffect(() => {
    if (!loginInitialized.current) {
      void loadLoginData();
      loginInitialized.current = true;
    }
  }, [loadLoginData]);

  return initialLoginData;
};
